import React, { useState, useCallback, useMemo } from 'react';
import { node } from 'prop-types';

import { errorLogger } from '@ajaibid/utils';
import { V4, V5 } from 'Util/fetcher';
import {
  postForgetPin,
  postValidatePin,
  postPinStatus,
  getPinData,
  getIdAvailibility,
} from './fetcher';

export const PinContext = React.createContext();

const PinProvider = ({ children }) => {
  const [isLoadingPin, setIsLoadingPin] = useState(false);
  const [isPinError, setIsPinError] = useState(false);
  const [isPinSuccess, setIsPinSuccess] = useState(false);
  const [isForgotPinSuccess, setIsForgotPinSuccess] = useState(false);
  const [isPinStatusLoading, setIsPinStatusLoading] = useState(false);
  const [pinStatusResult, setPinStatusResult] = useState(null);
  const [pinDataResult, setPinDataResult] = useState(null);
  const [isPinDataLoading, setIsPinDataLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [lockDuration, setLockDuration] = useState(0);
  const [isIdAvailable, setIsIdAvailable] = useState(false);
  const [idNumber, setIdNumber] = useState('');

  const pinStatus = useCallback(async () => {
    setIsPinStatusLoading(true);
    try {
      const response = await postPinStatus();
      setIsPinStatusLoading(false);
      setPinStatusResult(response.result);
    } catch (err) {
      setIsPinStatusLoading(false);
      errorLogger('ERROR_API', err.message);
    }
  }, [postPinStatus]);

  const pinData = useCallback(async () => {
    setIsPinDataLoading(true);
    try {
      const response = await getPinData();
      setPinDataResult(response.result);
      setIsPinDataLoading(false);
    } catch (err) {
      errorLogger('ERROR_API', err.message);
      setIsPinDataLoading(false);
    }
  }, [getPinData]);

  const forgetPin = useCallback(async () => {
    try {
      const response = await postForgetPin(V4);
      setIsForgotPinSuccess(true);
      return response;
    } catch (err) {
      errorLogger('ERROR_API', err.message);
    }
  }, [postForgetPin]);

  const forgetPinFlex = useCallback(
    async (payload) => {
      setIsLoadingPin(true);
      try {
        const version = pinDataResult?.is_pin_6digit ? V5 : V4;
        const response = await postForgetPin(version, payload);
        setIdNumber(payload?.id_number);
        if (
          !response?.result ||
          response?.result?.is_valid === false ||
          response.err_code !== 'EC0000000'
        ) {
          setErrorMessage(response?.err_message);
          setErrorCode(response?.err_code);
        } else {
          setIsForgotPinSuccess(true);
        }
        setIsLoadingPin(false);
      } catch (err) {
        errorLogger('ERROR_API', err.message);
        setErrorMessage(err?.message);
        setIsLoadingPin(false);
      }
    },
    [postForgetPin, pinDataResult]
  );

  const validatePin = useCallback(
    async (payload) => {
      setIsLoadingPin(true);
      try {
        const response = await postValidatePin(payload);

        if (
          !response?.result ||
          response?.result?.valid === false ||
          response.err_code !== 'EC0000000'
        ) {
          setIsPinError(true);
          setErrorMessage(response?.err_message ?? 'Ada yang salah, silahkan coba lagi');
          setLockDuration(response?.result?.locked_duration_minute);
        } else {
          setIsPinSuccess(true);
        }
        setIsLoadingPin(false);
      } catch (err) {
        setIsPinError(true);
        setIsLoadingPin(false);
        errorLogger('ERROR_API', err?.message);
        setErrorMessage(err?.message);
      }
    },
    [postValidatePin]
  );

  const checkIdAvailibility = useCallback(async () => {
    setIsPinDataLoading(true);
    try {
      const response = await getIdAvailibility();
      setIsIdAvailable(response.result?.has_kyc);
      setIsPinDataLoading(false);
    } catch (err) {
      errorLogger('ERROR_API', err.message);
      setIsPinDataLoading(false);
    }
  }, [getIdAvailibility]);

  const valueProps = useMemo(
    () => ({
      isLoadingPin,
      isPinError,
      isPinSuccess,
      isForgotPinSuccess,
      isPinStatusLoading,
      isPinDataLoading,
      setIsForgotPinSuccess,
      forgetPin,
      validatePin,
      pinStatus,
      pinData,
      pinDataResult,
      pinStatusResult,
      errorMessage,
      errorCode,
      lockDuration,
      isIdAvailable,
      checkIdAvailibility,
      forgetPinFlex,
      idNumber,
    }),
    [
      isLoadingPin,
      isPinError,
      isPinSuccess,
      isForgotPinSuccess,
      isPinStatusLoading,
      isPinDataLoading,
      setIsForgotPinSuccess,
      forgetPin,
      validatePin,
      pinStatus,
      pinData,
      pinDataResult,
      pinStatusResult,
      errorMessage,
      errorCode,
      lockDuration,
      isIdAvailable,
      checkIdAvailibility,
      forgetPinFlex,
      idNumber,
    ]
  );

  return <PinContext.Provider value={valueProps}>{children}</PinContext.Provider>;
};

PinProvider.propTypes = {
  children: node.isRequired,
};

export default PinProvider;
